import { Instance, types } from "mobx-state-tree";
import { observer } from "mobx-react";
import React from "react";
import classNames from "classnames";
import { BackgroundType, ChatItemBorder } from "../chat/chatItemBorder";
import styles from "./chatMessage.css";
import { FilePreviews } from "../files/filePreviews";
import { isHTMLAnchorElement, linkOnClick, ParsedMessage } from "./utils";

const showdown = require("showdown");
const converter = new showdown.Converter({
  simplifiedAutoLink: true,
  simpleLineBreaks: true,
  tables: true,
});

interface Props {
  text?: string;
  attachments?: Array<IAttachment>;
  fromBot?: boolean;
  fromAgent?: boolean;
  alignRight: boolean;
  isPreview?: boolean;
  onFileDownload?: (fileUrl: string, fileId?: string, fileName?: string) => void;
  onLinkClicked?: (link: string) => void;
}

export const Attachment = types.model("Attachment", {
  token: types.string,
  id: types.maybe(types.string),
  contentType: types.maybe(types.string),
  fileName: types.maybe(types.string),
  previews: types.array(types.string),
  size: types.maybe(types.number),
  metadata: types.maybe(types.frozen({}))
});

export type IAttachment = Instance<typeof Attachment>;

@observer
export class ChatMessage extends React.Component<Props> {
  renderAttachments() {
    const { attachments, alignRight, onFileDownload } = this.props;
    return (
      attachments && (
        <div className={styles.attachments}>
          <FilePreviews
            attachments={attachments}
            alignRight={alignRight}
            onFileDownload={onFileDownload}
          />
        </div>
      )
    );
  }

  renderText() {
    const { text, isPreview, onLinkClicked, fromBot, fromAgent } = this.props;
    const richText = this.prepareRichText({text, fromBot, fromAgent});

    return (
      richText && (
        <ChatItemBorder widgetStyle={false} backgroundType={this.getBorderBackgroundType()}>
          <div className={classNames(styles.text, { [styles["typing-preview-text"]]: isPreview })}>
            <div
              className={styles["message-body"]}
              onClick={(e) => {
                if (isHTMLAnchorElement(e.target) && onLinkClicked) {
                  onLinkClicked(e.target.href);
                }
              }}
            >
              {richText}
            </div>
          </div>
        </ChatItemBorder>
      )
    );
  }

  render() {
    return (
      <>
        {this.renderAttachments()}
        {this.renderText()}
      </>
    );
  }

  private prepareRichText = (args: {text?: string, fromBot?: boolean, fromAgent?: boolean}) => {
    const {text, fromBot, fromAgent} = args;
    if (text) {
      if (fromBot) {
        return (
          <div
            onClick={linkOnClick}
            dangerouslySetInnerHTML={{
              __html: converter.makeHtml(text).replace(/a href/g, "a target='_blank' href")
            }}
          />
        );
      }
      if (fromAgent) {
        return <ParsedMessage
          text={text}
          linkTarget="_blank"
          markdown={true}
        />;
      }
      return (
        <>
          {text.split("\n").map((part, idx) => <p key={idx}>{part}</p>)}
        </>
      );
    }
    return text;
  };

  private getBorderBackgroundType(): BackgroundType {
    if (this.props.fromBot) {
      return BackgroundType.BOT;
    } else if (this.props.fromAgent) {
      return BackgroundType.AGENT;
    } else if (this.props.alignRight) {
      return BackgroundType.CURRENT_USER_OR_ORG;
    } else if (this.props.isPreview) {
      return BackgroundType.TYPING_PREVIEW;
    } else {
      return BackgroundType.LEAD;
    }
  }
}
